/**
 * CALENDAR DATES, AS THE PERSON LOOKING AT THEM WOULD WRITE THEM.
 *
 * THE BUG THIS FIXES -- every date in the app was one day early.
 *
 * The app used `new Date(...).toISOString().slice(0, 10)` everywhere. That
 * converts the moment to UTC first, and India is UTC+5:30, so local midnight
 * is HALF PAST SIX THE PREVIOUS EVENING in UTC. Taking the first ten
 * characters then takes the previous day:
 *
 *     sheet says            29-04-2019
 *     Date object           Mon Apr 29 2019 00:00:00 GMT+0530
 *     toISOString()         2019-04-28T18:30:00.000Z
 *     .slice(0, 10)         2019-04-28          <-- stored
 *
 * Every NPA date imported from every spreadsheet was shifted back a day.
 * The same applies to anything stamped "today" between midnight and 05:30
 * IST: a collection recorded at 2am was dated yesterday, which would put it
 * in the wrong month on a commission bill.
 *
 * These helpers read the date in the LOCAL calendar and never convert. Use
 * them for anything a person reads as a date: NPA dates, collection dates,
 * PTP dates, closure dates, file names.
 *
 * NOT for timestamps. A moment in time -- `createdAt`, an audit trail entry
 * -- is correctly stored as a full ISO string with its offset, and
 * `toISOString()` is right for those. It is only taking the first ten
 * characters of one, and calling the result a date, that is wrong.
 */

/** `YYYY-MM-DD` as the local calendar reads it. */
export function toLocalDateString(value?: Date | string | number | null): string {
  if (value === undefined || value === null || value === '') return '';

  // A string that is already a plain date is returned untouched. Re-parsing
  // it would hand it to the Date constructor, which reads a bare
  // "2019-04-29" as UTC midnight and reintroduces the very shift this
  // function exists to remove.
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return trimmed;
    return formatLocal(parsed);
  }

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return formatLocal(d);
}

function formatLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Today, in the local calendar. Replaces `new Date().toISOString().slice(0, 10)`. */
export function todayLocal(): string {
  return formatLocal(new Date());
}

/**
 * NOW, IN THE LOCAL CALENDAR AND CLOCK: `YYYY-MM-DD HH:MM:SS`.
 *
 * THE FAULT THIS FIXES. Timestamps were written with
 * `new Date().toISOString()`, which is UTC. In India that is five and a
 * half hours behind, so every activity log, every sign-in and every audit
 * entry was stamped with a time that had not happened yet -- a sign-in at
 * 13:25 recorded as 07:55.
 *
 * The date was wrong too, not only the clock. Anything between midnight
 * and half past five in the morning falls on the PREVIOUS day in UTC, so
 * an agent signing in at 2am was counted against yesterday.
 *
 * Same shape as before, so anything that reads the first ten characters as
 * the day, or the next eight as the time, keeps working.
 */
export function nowLocalStamp(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/** The local month, `YYYY-MM`. Used by the month filters and commission bills. */
export function monthLocal(value?: Date | string | number | null): string {
  const s = value === undefined ? todayLocal() : toLocalDateString(value);
  return s.slice(0, 7);
}

/**
 * A DATE-ONLY CELL FROM A SPREADSHEET.
 *
 * THE BUG THIS FIXES -- and it is NOT the timezone one above. I fixed that
 * first and it changed nothing, because this is a different fault with the
 * same symptom.
 *
 * The spreadsheet library rebuilds a date-only cell from Excel's serial
 * number, and the arithmetic lands a few SECONDS short of midnight:
 *
 *     sheet cell        28-02-2019
 *     rebuilt as        Wed Feb 27 2019 23:59:50 IST
 *                                        ^^^^^^^^
 *
 * Ten seconds early, on the previous day. Reading the year, month and day
 * off that -- by ANY method, local or UTC -- gives 27 February. Every NPA
 * date in the app was a day early for this reason.
 *
 * So the day is rounded to the NEAREST midnight rather than truncated.
 * 23:59:50 rounds up to the next day; 00:00:10 stays where it is. An Excel
 * date-only cell has no time of day, so any time away from midnight is
 * error, and the nearest midnight is the date that was meant.
 *
 * ONLY FOR SPREADSHEET VALUES. `toLocalDateString` must NOT round: it is
 * used for things like "three days from now", where the time of day is real
 * and rounding would push the answer into the wrong day.
 */
export function excelDateToLocalString(value?: Date | string | number | null): string {
  if (value === undefined || value === null || value === '') return '';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    // Half a day forward, then read the calendar: the standard way to round
    // to the nearest day without touching the clock.
    return toLocalDateString(new Date(value.getTime() + 12 * 60 * 60 * 1000));
  }

  // Text and serial numbers are handled by the ordinary parser -- they carry
  // no time of day to drift.
  return toLocalDateString(value);
}

/**
 * AN EXCEL DATE CELL, COPIED ACROSS. NO CLOCK INVOLVED.
 *
 * WHY THIS EXISTS, AFTER TWO WRONG ATTEMPTS.
 *
 * A date cell in a spreadsheet is a DAY. It has no time of day, no time
 * zone, and no country. The app was turning it into a Date -- a precise
 * moment on a clock -- and then turning that back into a day. Every step of
 * that round trip can move the answer:
 *
 *   - converting to UTC took the previous day in India (fixed in Batch 72)
 *   - the library's own conversion starts from a base date in 1899, when
 *     India's clock was +5:21 rather than +5:30, so it lands 8 minutes 50
 *     seconds past midnight -- harmless here, but it would be a day out in
 *     any country west of Greenwich
 *
 * None of that should have been possible, because none of it is about the
 * borrower's NPA date. Excel stores a whole number -- 43467 -- and the only
 * honest answer is to turn that number into a day with arithmetic and copy
 * it across.
 *
 * So this uses integers only. No Date, no time zone, no clock. The same
 * spreadsheet gives the same answer on every machine in the world.
 *
 * Serial 0 is 30 December 1899, which absorbs Excel's own 1900 leap-year
 * mistake; every date from 1 March 1900 onwards is then exact.
 */
export function excelSerialToDateString(serial: number): string {
  if (!Number.isFinite(serial)) return '';
  // Whole days only: the fractional part is a time of day, which a date
  // cell does not have, and rounding is what the user sees in the cell.
  const days = Math.round(serial);
  if (days <= 0) return '';

  // Days since 1970-01-01, then the civil calendar, in integers.
  let z = days - 25569 + 719468; // Excel serial -> days since 1970 -> days since 0000-03-01 era
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;                                   // [0, 146096]
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100)); // [0, 365]
  const mp = Math.floor((5 * doy + 2) / 153);                     // [0, 11]
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;             // [1, 31]
  const m = mp + (mp < 10 ? 3 : -9);                              // [1, 12]
  const year = y + (m <= 2 ? 1 : 0);

  return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * Whatever a spreadsheet gives for a date cell, as `YYYY-MM-DD`.
 *
 * A serial number is converted by arithmetic. A Date -- which the library
 * produces when it is asked to -- is read by its own local calendar fields,
 * never by converting it. Text is left exactly as it was written.
 */
export function sheetDateToString(value?: Date | string | number | null): string {
  if (value === undefined || value === null || value === '') return '';

  if (typeof value === 'number') {
    // Excel serials for real dates sit well inside this range; anything else
    // is not a date and is left for the caller to deal with.
    if (value > 0 && value < 2958466) return excelSerialToDateString(value);
    return String(value);
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    // Its own calendar fields. Not toISOString, which is a different day.
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(
      value.getDate()
    ).padStart(2, '0')}`;
  }

  const text = String(value).trim();
  // A bare serial that arrived as text.
  if (/^\d{5}(\.\d+)?$/.test(text)) {
    const n = Number(text);
    if (n > 0 && n < 2958466) return excelSerialToDateString(n);
  }
  return text;
}

/**
 * A DATE AS INDIA WRITES IT: DD-MM-YYYY.
 *
 * Dates are STORED as YYYY-MM-DD, which sorts and compares correctly and is
 * the only sane format for a database. But the screens were printing that
 * stored string straight out in some places and a DD-MM-YYYY version in
 * others, so the SAME date read "2019-01-02" on the accounts list and
 * "02-01-2019" in the OTS window. Two formats for one date, one of them
 * easily misread as 1 February.
 *
 * Every screen now prints this. Storage is unchanged.
 *
 * Text that is not a plain YYYY-MM-DD date is returned untouched, so "N/A",
 * a blank, or a date a bank wrote some other way is never mangled.
 */
export function displayDate(value?: string | null): string {
  const s = (value || '').trim();
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[3]}-${m[2]}-${m[1]}`;
}
